// src/services/integrationConfig.ts
// ============================================
// XEAOS 集成中心热插拔配置系统（设置中心 P1 深度扩展）
//
// 统一管理三类配置，全部支持运行时热插拔（订阅/通知，无需重启）：
//   1. API 开放平台：API Key（作用域/状态）、第三方应用、限流规则
//   2. 第三方集成：企业微信/钉钉/飞书/邮件/短信/对象存储 的连接状态
//   3. Webhook 与事件系统：事件订阅（事件→端点）、投递日志
//
// 持久化：localStorage（浏览器本地）+ Vault 同步（可选，通过 /vault-api/write）
// 热插拔：subscribe(callback) 订阅变更，修改后自动 notify
// ============================================

export interface ApiKeyRecord {
  id: string;
  key: string;
  name: string;
  scope: "read" | "write" | "admin";
  status: "active" | "revoked";
  app: string;
  createdAt: string;
  lastUsedAt?: string;
  callCount: number;
}

export interface ApiAppRecord {
  id: string;
  name: string;
  description: string;
  scopes: string[];
  status: "active" | "disabled";
  owner: string;
  createdAt: string;
}

export interface RateLimitRule {
  id: string;
  scope: string;
  perMinute: number;
  perHour: number;
  enabled: boolean;
}

export interface IntegrationChannel {
  id: string;
  name: string;
  icon: string;
  category: string;
  connected: boolean;
  config: Record<string, string>;
  description: string;
  connectedAt?: string;
  origin: "builtin" | "market";
}

export interface WebhookSubscription {
  id: string;
  event: string;
  url: string;
  secret: string;
  enabled: boolean;
  maxRetries: number;
  createdAt: string;
}

export interface WebhookLogEntry {
  id: string;
  event: string;
  url: string;
  status: "success" | "failed" | "retrying";
  attempts: number;
  statusCode?: number;
  response?: string;
  timestamp: string;
  durationMs: number;
}

export interface EventDefinition {
  id: string;
  label: string;
  category: string;
  description: string;
}

export const EVENT_CATALOG: EventDefinition[] = [
  { id: "content.published", label: "内容已发布", category: "内容", description: "任何平台内容发布成功后触发" },
  { id: "content.media_generated", label: "媒体生成完成", category: "内容", description: "图片/视频/音乐生成完成后触发" },
  { id: "customer.created", label: "新客户建档", category: "客户", description: "CRM 新建客户档案时触发" },
  { id: "customer.followup_due", label: "跟进提醒到期", category: "客户", description: "客户跟进提醒到达时触发" },
  { id: "crm.payment_received", label: "收款记录", category: "财务", description: "财务中心记录收款时触发" },
  { id: "finance.expense_recorded", label: "支出记录", category: "财务", description: "财务中心记录支出时触发" },
  { id: "workflow.completed", label: "工作流执行完成", category: "工作流", description: "GEO/DSO/GF 或任意工作流执行完成时触发" },
  { id: "workflow.failed", label: "工作流执行失败", category: "工作流", description: "工作流执行失败时触发" },
  { id: "knowledge.evolved", label: "知识演化完成", category: "知识", description: "知识演化 Runtime 夜间整理完成时触发" },
  { id: "hermes.task_completed", label: "Hermes 任务完成", category: "Hermes", description: "Hermes 任务执行完成时触发" },
  { id: "system.health_warning", label: "系统健康告警", category: "系统", description: "服务健康度异常时触发" },
  { id: "custom.event", label: "自定义事件", category: "自定义", description: "通过自定义规则触发的事件" },
];

const STORAGE_KEYS = {
  apiKeys: "xeaos_api_platform_keys",
  apiApps: "xeaos_api_platform_apps",
  rateLimits: "xeaos_api_platform_rate_limits",
  integrations: "xeaos_integration_channels",
  webhookSubs: "xeaos_webhook_subscriptions",
  webhookLogs: "xeaos_webhook_logs",
};

const DEFAULT_INTEGRATIONS: IntegrationChannel[] = [
  { id: "wechat_work", name: "企业微信", icon: "MessageSquare", category: "im", connected: false, config: {}, description: "客户群运营 / 员工通知 / 消息推送", origin: "builtin" },
  { id: "dingtalk", name: "钉钉", icon: "Bell", category: "im", connected: false, config: {}, description: "审批通知 / 群机器人 / 日程同步", origin: "builtin" },
  { id: "feishu", name: "飞书", icon: "Send", category: "im", connected: false, config: {}, description: "多维表格 / 消息推送 / 会议纪要", origin: "builtin" },
  { id: "email_smtp", name: "邮件 SMTP", icon: "Mail", category: "notify", connected: false, config: {}, description: "营销邮件 / 客户通知 / 报表推送", origin: "builtin" },
  { id: "sms_aliyun", name: "短信（阿里云）", icon: "Smartphone", category: "notify", connected: false, config: {}, description: "验证码 / 营销短信 / 服务通知", origin: "builtin" },
  { id: "storage_oss", name: "对象存储 OSS", icon: "Cloud", category: "storage", connected: false, config: {}, description: "阿里云 OSS，媒体文件托管", origin: "builtin" },
  { id: "storage_qiniu", name: "对象存储 七牛云", icon: "Cloud", category: "storage", connected: false, config: {}, description: "七牛云 Kodo，图片/视频托管", origin: "builtin" },
  { id: "storage_upyun", name: "对象存储 又拍云", icon: "Cloud", category: "storage", connected: false, config: {}, description: "又拍云，CDN 加速分发", origin: "builtin" },
  { id: "erp_jindie", name: "金蝶 ERP", icon: "Database", category: "erp", connected: false, config: {}, description: "财务凭证同步 / 库存对接", origin: "market" },
  { id: "erp_yonyou", name: "用友 ERP", icon: "Database", category: "erp", connected: false, config: {}, description: "财务/供应链数据对接", origin: "market" },
  { id: "crm_wechat", name: "微信客服", icon: "Headset", category: "crm", connected: false, config: {}, description: "微信客服消息接入 CRM", origin: "market" },
  { id: "notion", name: "Notion", icon: "Notebook", category: "knowledge", connected: false, config: {}, description: "文档同步 / 知识导入", origin: "market" },
];

const DEFAULT_RATE_LIMITS: RateLimitRule[] = [
  { id: "rl_read", scope: "read", perMinute: 300, perHour: 10000, enabled: true },
  { id: "rl_write", scope: "write", perMinute: 120, perHour: 3000, enabled: true },
  { id: "rl_admin", scope: "admin", perMinute: 30, perHour: 500, enabled: true },
];

function genId(prefix: string): string {
  const rand = Math.random().toString(36).slice(2, 8);
  const time = Date.now().toString(36).slice(-4);
  return `${prefix}_${time}${rand}`;
}

function genSecret(prefix: string, len = 32): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let s = prefix + "_";
  for (let i = 0; i < len; i++) {
    s += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return s;
}

function maskKey(key: string): string {
  if (key.length <= 12) return "••••••••";
  return `${key.slice(0, 8)}••••${key.slice(-4)}`;
}



// ============================================
// 集成配置管理器（热插拔核心）
// ============================================

class IntegrationConfigManager {
  private apiKeys: ApiKeyRecord[] = [];
  private apiApps: ApiAppRecord[] = [];
  private rateLimits: RateLimitRule[] = [...DEFAULT_RATE_LIMITS];
  private integrations: IntegrationChannel[] = [...DEFAULT_INTEGRATIONS];
  private webhookSubs: WebhookSubscription[] = [];
  private webhookLogs: WebhookLogEntry[] = [];
  private listeners: Set<() => void> = new Set();

  constructor() {
    this.load();
  }

  private load() {
    try {
      this.apiKeys = JSON.parse(localStorage.getItem(STORAGE_KEYS.apiKeys) || "[]");
      this.apiApps = JSON.parse(localStorage.getItem(STORAGE_KEYS.apiApps) || "[]");
      this.rateLimits = JSON.parse(localStorage.getItem(STORAGE_KEYS.rateLimits) || "[]");
      if (this.rateLimits.length === 0) this.rateLimits = [...DEFAULT_RATE_LIMITS];
      this.integrations = JSON.parse(localStorage.getItem(STORAGE_KEYS.integrations) || "[]");
      if (this.integrations.length === 0) this.integrations = [...DEFAULT_INTEGRATIONS];
      this.webhookSubs = JSON.parse(localStorage.getItem(STORAGE_KEYS.webhookSubs) || "[]");
      this.webhookLogs = JSON.parse(localStorage.getItem(STORAGE_KEYS.webhookLogs) || "[]");
    } catch {
      // 忽略
    }
  }

  private save() {
    try {
      localStorage.setItem(STORAGE_KEYS.apiKeys, JSON.stringify(this.apiKeys));
      localStorage.setItem(STORAGE_KEYS.apiApps, JSON.stringify(this.apiApps));
      localStorage.setItem(STORAGE_KEYS.rateLimits, JSON.stringify(this.rateLimits));
      localStorage.setItem(STORAGE_KEYS.integrations, JSON.stringify(this.integrations));
      localStorage.setItem(STORAGE_KEYS.webhookSubs, JSON.stringify(this.webhookSubs));
      localStorage.setItem(STORAGE_KEYS.webhookLogs, JSON.stringify(this.webhookLogs));
    } catch {
      // 忽略
    }
    this.notify();
  }

  subscribe(callback: () => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private notify() {
    this.listeners.forEach((cb) => {
      try {
        cb();
      } catch {
        // 忽略
      }
    });
  }

  // ============ API 平台 ============

  getApiKeys(): ApiKeyRecord[] {
    return [...this.apiKeys];
  }

  createApiKey(name: string, scope: ApiKeyRecord["scope"], app: string): ApiKeyRecord {
    const key = genSecret("xk", 32);
    const record: ApiKeyRecord = {
      id: genId("k"),
      key,
      name: name || "未命名密钥",
      scope,
      status: "active",
      app: app || "内部应用",
      createdAt: new Date().toISOString(),
      callCount: 0,
    };
    this.apiKeys = [record, ...this.apiKeys];
    this.save();
    return record;
  }

  revokeApiKey(id: string): void {
    this.apiKeys = this.apiKeys.map((k) => (k.id === id ? { ...k, status: "revoked" as const } : k));
    this.save();
  }

  recordApiCall(id: string): void {
    this.apiKeys = this.apiKeys.map((k) =>
      k.id === id
        ? { ...k, callCount: k.callCount + 1, lastUsedAt: new Date().toISOString() }
        : k
    );
    this.save();
  }

  getApiApps(): ApiAppRecord[] {
    return [...this.apiApps];
  }

  addApiApp(name: string, description: string, scopes: string[]): void {
    this.apiApps = [
      { id: genId("app"), name, description, scopes, status: "active", owner: "本地", createdAt: new Date().toISOString() },
      ...this.apiApps,
    ];
    this.save();
  }

  getRateLimits(): RateLimitRule[] {
    return [...this.rateLimits];
  }

  updateRateLimit(id: string, patch: Partial<RateLimitRule>): void {
    this.rateLimits = this.rateLimits.map((r) => (r.id === id ? { ...r, ...patch } : r));
    this.save();
  }

  // ============ 第三方集成 ============

  getIntegrations(): IntegrationChannel[] {
    return [...this.integrations];
  }

  getIntegration(id: string): IntegrationChannel | undefined {
    return this.integrations.find((c) => c.id === id);
  }

  connectIntegration(id: string, config: Record<string, string>): void {
    this.integrations = this.integrations.map((c) =>
      c.id === id
        ? { ...c, connected: true, config: { ...c.config, ...config }, connectedAt: new Date().toISOString() }
        : c
    );
    this.save();
  }

  disconnectIntegration(id: string): void {
    this.integrations = this.integrations.map((c) =>
      c.id === id ? { ...c, connected: false, config: {}, connectedAt: undefined } : c
    );
    this.save();
  }

  updateIntegrationConfig(id: string, patch: Record<string, string>): void {
    this.integrations = this.integrations.map((c) =>
      c.id === id ? { ...c, config: { ...c.config, ...patch } } : c
    );
    this.save();
  }

  // ============ Webhook 与事件系统 ============

  getWebhookSubscriptions(): WebhookSubscription[] {
    return [...this.webhookSubs];
  }

  getWebhookLogs(): WebhookLogEntry[] {
    return [...this.webhookLogs];
  }

  addWebhookSubscription(event: string, url: string, secret: string, maxRetries = 3): void {
    this.webhookSubs = [
      { id: genId("wh"), event, url, secret: secret || "", enabled: true, maxRetries, createdAt: new Date().toISOString() },
      ...this.webhookSubs,
    ];
    this.save();
  }

  toggleWebhookSubscription(id: string, enabled: boolean): void {
    this.webhookSubs = this.webhookSubs.map((s) => (s.id === id ? { ...s, enabled } : s));
    this.save();
  }

  removeWebhookSubscription(id: string): void {
    this.webhookSubs = this.webhookSubs.filter((s) => s.id !== id);
    this.save();
  }

  logWebhookDelivery(entry: Omit<WebhookLogEntry, "id" | "timestamp">): void {
    this.webhookLogs = [
      { ...entry, id: genId("log"), timestamp: new Date().toISOString() },
      ...this.webhookLogs,
    ].slice(0, 200);
    this.save();
  }

  clearWebhookLogs(): void {
    this.webhookLogs = [];
    this.save();
  }

  getStats() {
    const activeKeys = this.apiKeys.filter((k) => k.status === "active").length;
    const connected = this.integrations.filter((c) => c.connected).length;
    const activeWebhooks = this.webhookSubs.filter((s) => s.enabled).length;
    const totalCalls = this.apiKeys.reduce((s, k) => s + k.callCount, 0);
    const okLogs = this.webhookLogs.filter((l) => l.status === "success").length;
    return { activeKeys, connected, activeWebhooks, totalCalls, okLogs, totalLogs: this.webhookLogs.length };
  }
}

export const integrationConfig = new IntegrationConfigManager();
export { maskKey, genSecret, genId };
export default integrationConfig;
